/**
 * Klien API.
 *
 * Antarmuka lama memanggil fetch() langsung di 40-an tempat, masing-masing
 * dengan penanganan galatnya sendiri — sebagian menampilkan alert(), sebagian
 * diam saja, sebagian lagi menuliskan "undefined" ke layar. Di sini seluruh
 * permintaan melewati satu jalur: satu bentuk galat, satu cara membatalkan,
 * dan tipe yang dibagikan dengan server.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Sesi habis atau belum masuk — pemanggil biasanya perlu mengalihkan ke login. */
  get perluMasuk(): boolean {
    return this.status === 401
  }

  get ditolak(): boolean {
    return this.status === 403
  }

  get tidakDitemukan(): boolean {
    return this.status === 404
  }
}

interface RequestOptions {
  signal?: AbortSignal
  /** Dikirim sebagai JSON. Untuk unggahan berkas, pakai `form`. */
  json?: unknown
  form?: FormData
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
}

async function minta<T>(path: string, opsi: RequestOptions = {}): Promise<T> {
  const { signal, json, form, method } = opsi

  const init: RequestInit = {
    method: method ?? (json !== undefined || form ? 'POST' : 'GET'),
    // Cookie sesi wajib ikut; tanpa ini setiap permintaan dianggap anonim.
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    ...(signal ? { signal } : {}),
  }

  if (json !== undefined) {
    init.headers = { ...init.headers, 'Content-Type': 'application/json' }
    init.body = JSON.stringify(json)
  } else if (form) {
    // Content-Type sengaja tidak diisi: peramban harus menetapkannya sendiri
    // agar batas multipart ikut tertulis.
    init.body = form
  }

  let res: Response
  try {
    res = await fetch(path, init)
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err
    throw new ApiError(
      'Tidak dapat menghubungi server. Periksa koneksi internet Anda.',
      0,
    )
  }

  if (res.status === 204) return undefined as T

  const tipe = res.headers.get('content-type') ?? ''
  const isJson = tipe.includes('application/json')
  const body: unknown = isJson ? await res.json().catch(() => null) : await res.text()

  if (!res.ok) {
    const pesan =
      (isJson && typeof body === 'object' && body !== null && 'error' in body
        ? String((body as { error: unknown }).error)
        : null) ?? pesanBawaan(res.status)
    throw new ApiError(pesan, res.status, body)
  }

  return body as T
}

function pesanBawaan(status: number): string {
  switch (status) {
    case 401: return 'Sesi Anda sudah berakhir. Silakan masuk kembali.'
    case 403: return 'Anda tidak memiliki akses ke sini.'
    case 404: return 'Data yang Anda cari tidak ditemukan.'
    case 413: return 'Ukuran data melebihi batas yang diizinkan.'
    case 429: return 'Terlalu banyak permintaan. Silakan tunggu sebentar.'
    case 503: return 'Server sedang sibuk. Silakan coba lagi.'
    default:
      return status >= 500
        ? 'Terjadi kesalahan pada server.'
        : 'Permintaan tidak dapat diproses.'
  }
}

function qs(params: Record<string, string | number | undefined | null>): string {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

// ── Tipe bersama ────────────────────────────────────────────────────────────

export interface Me {
  id: number
  name: string
  nij: string | null
  email: string | null
  is_admin: boolean
  quota_bytes: number
  used_bytes: number
}

export interface DirEntry {
  name: string
  path: string
  is_dir: boolean
  size: number | null
  modified: number
  ext: string | null
  mime: string | null
}

export interface ShareMeta {
  token: string
  label: string | null
  is_folder: boolean
  permission: 'read' | 'write'
  access_type: 'public' | 'login_required' | 'selected_nij'
  expires_at: string | null
  exists: boolean
  name: string
}

export interface UserdataItem {
  name: string
  is_dir: boolean
  size: number | null
  size_fmt: string | null
  modified: number
  ext: string | null
  icon: string
  mime: string | null
  isImage: boolean
  canView: boolean
}

export interface UserdataListing {
  success: true
  items: UserdataItem[]
  path: string
  quota: number
  used: number
}

export type TipeAkses = 'public' | 'login_required' | 'selected_nij'

export interface LinkRingkas {
  id: number
  token: string
  path: string
  is_folder: boolean
  access_type: TipeAkses
  permission: 'read' | 'write'
  label: string | null
  created_by: number | null
  creator_name: string | null
  expires_at: string | null
  created_at: string
  milik_saya: boolean
  akses_count: number
  permintaan_menunggu: number
  kedaluwarsa: boolean
}

export interface LinkDetail extends Omit<LinkRingkas, 'milik_saya' | 'akses_count' | 'permintaan_menunggu' | 'kedaluwarsa' | 'creator_name'> {
  nij_list: string[]
}

export interface AksesLink {
  id: number
  first_accessed: string
  last_accessed: string
  name: string
  nij: string | null
}

export interface Bintang {
  id: number
  type: 'link' | 'file'
  link_id: number | null
  file_path: string | null
  label: string | null
  starred_at: string
  token: string | null
  link_label: string | null
  link_path: string | null
  is_folder: boolean
  access_type: TipeAkses | null
  expires_at: string | null
  yatim: boolean
  kedaluwarsa: boolean
}

export interface PermintaanAkses {
  id: number
  status: 'pending' | 'approved' | 'rejected'
  message: string | null
  created_at: string
  user_name: string
  user_nij: string | null
  link_token: string
  link_label: string | null
  link_path: string
  link_owner: number | null
}

export interface DibagikanKeSaya {
  id: number
  first_accessed: string
  last_accessed: string
  link_id: number
  token: string
  label: string | null
  path: string
  is_folder: boolean
  access_type: TipeAkses
  expires_at: string | null
  creator_name: string | null
  kedaluwarsa: boolean
}

export interface AdminUser {
  id: number
  mygms_id: string
  nij: string | null
  name: string
  email: string | null
  is_admin: boolean
  is_suspended: boolean
  suspend_reason: string | null
  quota_bytes: number
  used_bytes: number
  created_at: string
  jumlah_tautan: number
}

export interface PermintaanKuota {
  id: number
  requested_bytes: number
  reason: string | null
  status: 'pending' | 'approved' | 'rejected'
  admin_note: string | null
  created_at: string
  user_id: number
  user_name: string
  user_nij: string | null
  quota_bytes: number
  used_bytes: number
}

export interface IsiZip {
  name: string
  size: number
  compressed_size: number
  modified: number
}

export interface ExifInfo {
  camera?: string
  lens?: string
  shutter?: string
  aperture?: string
  iso?: string
  focal?: string
  ev?: string
  date?: string
  width?: number
  height?: number
  gps?: { lat: number; lng: number }
}

/**
 * Kirim permintaan POST lewat formulir tersembunyi, sehingga peramban
 * memperlakukan responsnya sebagai unduhan berkas alih-alih data yang harus
 * ditampung di memori.
 *
 * Server menerima JSON, jadi muatannya dititipkan pada satu bidang bernama
 * `payload` — lihat penangan zip di sisi server yang membaca keduanya.
 */
function kirimFormUnduhan(action: string, payload: unknown): void {
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = action
  form.style.display = 'none'

  const field = document.createElement('input')
  field.type = 'hidden'
  field.name = 'payload'
  field.value = JSON.stringify(payload)
  form.appendChild(field)

  document.body.appendChild(form)
  form.submit()
  // Formulir dibuang setelah peramban sempat mengirimnya.
  setTimeout(() => form.remove(), 1000)
}

// ── Titik akhir ─────────────────────────────────────────────────────────────

export const api = {
  me: (signal?: AbortSignal) => minta<Me>('/api/v1/me', signal ? { signal } : {}),

  files: {
    list: (path: string, signal?: AbortSignal) =>
      minta<{ path: string; items: DirEntry[] }>(
        `/api/v1/files${qs({ path })}`,
        signal ? { signal } : {},
      ),
  },

  share: {
    meta: (token: string, signal?: AbortSignal) =>
      minta<ShareMeta>(`/api/v1/share/${token}/meta`, signal ? { signal } : {}),

    list: (token: string, dir: string, signal?: AbortSignal) =>
      minta<{ dir: string; items: DirEntry[] }>(
        `/api/v1/share/${token}/list${qs({ dir })}`,
        signal ? { signal } : {},
      ),

    exif: (token: string, file: string, signal?: AbortSignal) =>
      minta<ExifInfo>(
        `/api/v1/share/${token}/exif${qs({ file })}`,
        signal ? { signal } : {},
      ),

    /**
     * Unduh beberapa berkas sebagai ZIP.
     *
     * Sengaja TIDAK memakai fetch: arsipnya bisa berukuran puluhan gigabyte,
     * dan fetch akan menampungnya di memori sebagai Blob lebih dulu sampai
     * tab peramban kehabisan memori. Dengan mengirim formulir tersembunyi,
     * peramban menanganinya sebagai unduhan biasa — lengkap dengan indikator
     * kemajuan bawaan dan kemampuan melanjutkan.
     */
    zip: (token: string, files: string[]) => kirimFormUnduhan(`/api/v1/share/${token}/zip`, { files }),

    zipInfo: (token: string, file: string, signal?: AbortSignal) =>
      minta<{ count: number; entries: IsiZip[] }>(
        `/api/v1/share/${token}/zip-info${qs({ file })}`,
        signal ? { signal } : {},
      ),

    /** Kirim permintaan akses atas tautan yang dibatasi. */
    mintaAkses: (token: string, message?: string) =>
      minta<{ success: true }>('/api/v1/access-requests', {
        json: { token, ...(message ? { message } : {}) },
      }),

    downloadUrl: (token: string, file?: string) =>
      minta<{ url: string; expires_in: number }>(`/api/v1/share/${token}/download-url`, {
        json: { file },
      }),

    /** URL langsung — dipakai sebagai href/src, bukan lewat fetch. */
    fileUrl: (token: string, file?: string) => `/s/${token}/dl${qs({ file })}`,
    viewUrl: (token: string, file?: string) => `/s/${token}/view${qs({ file })}`,
    thumbUrl: (token: string, file: string, size = 240) =>
      `/api/v1/share/${token}/thumb${qs({ file, s: size })}`,
    previewUrl: (token: string, file: string, w = 1600, h = 900) =>
      `/api/v1/share/${token}/thumb${qs({ file, mode: 'fit', w, h })}`,
    /** Konversi HEIF ke JPEG resolusi penuh — untuk perangkat lunak lama. */
    jpegUrl: (token: string, file: string) => `/s/${token}/jpeg${qs({ file })}`,
  },

  userdata: {
    list: (path: string, signal?: AbortSignal) =>
      minta<UserdataListing>(`/api/v1/userdata/list${qs({ path })}`, signal ? { signal } : {}),

    quota: (signal?: AbortSignal) =>
      minta<{ success: true; quota: number; used: number }>(
        '/api/v1/userdata/quota',
        signal ? { signal } : {},
      ),

    recalculate: () =>
      minta<{ success: true; used: number; quota: number }>('/api/v1/userdata/recalculate', {
        method: 'POST',
      }),

    readFile: (path: string, signal?: AbortSignal) =>
      minta<{ success: true; content: string; size: number }>(
        `/api/v1/userdata/file${qs({ path })}`,
        signal ? { signal } : {},
      ),

    createFile: (path: string, filename: string) =>
      minta<{ success: true; path: string }>('/api/v1/userdata/file', {
        json: { path, filename },
      }),

    saveFile: (path: string, content: string) =>
      minta<{ success: true; size: number }>('/api/v1/userdata/file', {
        method: 'PUT',
        json: { path, content },
      }),

    createFolder: (path: string, name: string) =>
      minta<{ success: true }>('/api/v1/userdata/folder', { json: { path, name } }),

    remove: (path: string) =>
      minta<{ success: true }>('/api/v1/userdata/item', { method: 'DELETE', json: { path } }),

    rename: (path: string, newName: string) =>
      minta<{ success: true }>('/api/v1/userdata/rename', {
        json: { path, new_name: newName },
      }),

    move: (src: string, dest: string) =>
      minta<{ success: true }>('/api/v1/userdata/move', { json: { src, dest } }),

    copy: (src: string, dest: string) =>
      minta<{ success: true }>('/api/v1/userdata/copy', { json: { src, dest } }),

    batchMove: (paths: string[], dest: string) =>
      minta<{ success: boolean; moved: number; failed: number }>(
        '/api/v1/userdata/batch-move',
        { json: { paths, dest } },
      ),

    batchCopy: (paths: string[], dest: string) =>
      minta<{ success: boolean; copied: number; failed: number }>(
        '/api/v1/userdata/batch-copy',
        { json: { paths, dest } },
      ),

    exif: (path: string, signal?: AbortSignal) =>
      minta<ExifInfo>(`/api/v1/userdata/exif${qs({ path })}`, signal ? { signal } : {}),

    zipInfo: (path: string, signal?: AbortSignal) =>
      minta<{ count: number; entries: IsiZip[] }>(
        `/api/v1/userdata/zip-info${qs({ path })}`,
        signal ? { signal } : {},
      ),

    serveUrl: (path: string, download = false) =>
      `/api/v1/userdata/serve${qs({ path, download: download ? '1' : undefined })}`,
    thumbUrl: (path: string, size = 240) =>
      `/api/v1/userdata/thumb${qs({ path, s: size })}`,
    previewUrl: (path: string, w = 1600, h = 900) =>
      `/api/v1/userdata/thumb${qs({ path, mode: 'fit', w, h })}`,
    /** Konversi HEIF ke JPEG resolusi penuh — untuk perangkat lunak lama. */
    jpegUrl: (path: string) => `/api/v1/userdata/jpeg${qs({ path })}`,
  },

  links: {
    daftar: (scope: 'mine' | 'all' = 'mine', signal?: AbortSignal) =>
      minta<{ links: LinkRingkas[] }>(`/api/v1/links${qs({ scope })}`, signal ? { signal } : {}),

    detail: (id: number, signal?: AbortSignal) =>
      minta<LinkDetail>(`/api/v1/links/${id}`, signal ? { signal } : {}),

    akses: (id: number, signal?: AbortSignal) =>
      minta<{ accesses: AksesLink[] }>(`/api/v1/links/${id}/accesses`, signal ? { signal } : {}),

    buat: (body: {
      path: string
      is_folder?: boolean
      access_type?: TipeAkses
      permission?: 'read' | 'write'
      label?: string
      expires_at?: string | null
      nij_list?: string[]
    }) => minta<{ id: number; token: string }>('/api/v1/links', { json: body }),

    ubah: (
      id: number,
      body: Partial<{
        label: string
        access_type: TipeAkses
        permission: 'read' | 'write'
        expires_at: string | null
        nij_list: string[]
      }>,
    ) => minta<LinkDetail>(`/api/v1/links/${id}`, { method: 'PUT', json: body }),

    hapus: (id: number) => minta<{ success: true }>(`/api/v1/links/${id}`, { method: 'DELETE' }),
  },

  stars: {
    daftar: (signal?: AbortSignal) =>
      minta<{ stars: Bintang[] }>('/api/v1/stars', signal ? { signal } : {}),

    toggleTautan: (linkId: number, label?: string) =>
      minta<{ starred: boolean }>('/api/v1/stars', {
        json: { type: 'link', link_id: linkId, ...(label ? { label } : {}) },
      }),

    toggleBerkas: (filePath: string, label?: string) =>
      minta<{ starred: boolean }>('/api/v1/stars', {
        json: { type: 'file', file_path: filePath, ...(label ? { label } : {}) },
      }),

    hapus: (id: number) => minta<{ success: true }>(`/api/v1/stars/${id}`, { method: 'DELETE' }),
  },

  permintaan: {
    daftar: (signal?: AbortSignal) =>
      minta<{ requests: PermintaanAkses[] }>('/api/v1/access-requests', signal ? { signal } : {}),

    putuskan: (id: number, status: 'approved' | 'rejected') =>
      minta<{ success: true }>(`/api/v1/access-requests/${id}`, { json: { status } }),
  },

  dibagikan: (signal?: AbortSignal) =>
    minta<{ items: DibagikanKeSaya[] }>('/api/v1/shared-with-me', signal ? { signal } : {}),

  admin: {
    users: (signal?: AbortSignal) =>
      minta<{ users: AdminUser[] }>('/api/v1/admin/users', signal ? { signal } : {}),

    ubahUser: (
      id: number,
      body: Partial<{
        quota_bytes: number
        is_admin: boolean
        is_suspended: boolean
        suspend_reason: string | null
      }>,
    ) => minta<{ success: true }>(`/api/v1/admin/users/${id}`, { method: 'PUT', json: body }),

    hitungUlang: (id: number) =>
      minta<{ success: true; used: number }>(`/api/v1/admin/users/${id}/recalculate`, {
        method: 'POST',
      }),

    permintaanKuota: (signal?: AbortSignal) =>
      minta<{ requests: PermintaanKuota[] }>(
        '/api/v1/admin/quota-requests',
        signal ? { signal } : {},
      ),

    putuskanKuota: (id: number, status: 'approved' | 'rejected', admin_note?: string) =>
      minta<{ success: true }>(`/api/v1/admin/quota-requests/${id}`, {
        json: { status, ...(admin_note ? { admin_note } : {}) },
      }),

    ringkasan: (signal?: AbortSignal) =>
      minta<{ pengguna: number; tautan: number; permintaan_akses: number; permintaan_kuota: number }>(
        '/api/v1/admin/ringkasan',
        signal ? { signal } : {},
      ),
  },

  kuota: {
    milikSaya: (signal?: AbortSignal) =>
      minta<{ requests: PermintaanKuota[]; menunggu: boolean }>(
        '/api/v1/quota-requests/mine',
        signal ? { signal } : {},
      ),

    ajukan: (requested_bytes: number, reason?: string) =>
      minta<{ success: true; diperbarui: boolean }>('/api/v1/quota-requests', {
        json: { requested_bytes, ...(reason ? { reason } : {}) },
      }),
  },

  uploads: {
    init: (body: {
      filename: string
      size: number
      upload_type?: 'userdata' | 'shared' | 'nas'
      subdir?: string
      token?: string
      target_dir?: string
      target_path?: string
    }) =>
      minta<{ success: true; upload_id: string; chunk_size: number; filename: string }>(
        '/api/v1/uploads',
        { json: body },
      ),

    chunk: (uploadId: string, index: number, blob: Blob, signal?: AbortSignal) => {
      const form = new FormData()
      form.append('chunk', blob)
      return minta<{ success: true; index: number }>(
        `/api/v1/uploads/${uploadId}/chunks/${index}`,
        { method: 'PUT', form, ...(signal ? { signal } : {}) },
      )
    },

    finalize: (uploadId: string) =>
      minta<{ success: true; filename: string; size: number }>(
        `/api/v1/uploads/${uploadId}/finalize`,
        { method: 'POST' },
      ),

    cancel: (uploadId: string) =>
      minta<{ success: true }>(`/api/v1/uploads/${uploadId}`, { method: 'DELETE' }),
  },
}

/** Format ukuran berkas — sama dengan formatBytes() di sisi server. */
export function formatBytes(bytes: number, precision = 2): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const value = Math.max(bytes, 0)
  if (value === 0) return '0 B'
  const pow = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1)
  return `${Number((value / 1024 ** pow).toFixed(precision))} ${units[pow]}`
}

const BULAN_ID = [
  'Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun',
  'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des',
]

/** Tanggal ringkas berbahasa Indonesia dari epoch detik. */
export function formatTanggal(epochSeconds: number): string {
  if (!epochSeconds) return '—'
  const d = new Date(epochSeconds * 1000)
  const sekarang = new Date()
  const selisihHari = Math.floor((sekarang.getTime() - d.getTime()) / 86_400_000)

  const jam = `${String(d.getHours()).padStart(2, '0')}.${String(d.getMinutes()).padStart(2, '0')}`
  if (selisihHari === 0) return `Hari ini, ${jam}`
  if (selisihHari === 1) return `Kemarin, ${jam}`
  if (selisihHari < 7) return `${selisihHari} hari lalu`

  const tahun = d.getFullYear() === sekarang.getFullYear() ? '' : ` ${d.getFullYear()}`
  return `${d.getDate()} ${BULAN_ID[d.getMonth()]}${tahun}`
}
